import React, { useMemo, memo } from 'react'
import BulletChart from 'Components/Stats/BulletChartRaw'
import { getAppropriateRange } from './bulletChat_util'
import { isSmallScreen } from 'Utils/Misc'

/**
 * @typedef BulletChartWrapperProps
 * @property {number} actualValue This is the point where the solid bar will
 * end.
 * @property {string} actualValueClassName Optionally re-style the actual value
 * label
 * @property {number} targetValue This is shown as an indicator on the chart.
 * It helps to show how much the actual value has surpassed it or not reached
 * it.
 * @property {boolean} showAsPercent Whether or not the values are percentages,
 * shows the indicator lables as percentages if so.
 *
 * @param {BulletChartWrapperProps} props
 */
const BulletChartWrapper = props => {
  var {
    actualValue,
    targetValue,
    className,
    actualValueClassName,
    children,
    suffix,
  } = props

  const {
    markers,
    axisLabels,
    percentFilled,
    actualValueLabel,
  } = useMemo(() => {
    var markers = []
    var axisLabels = []

    if (!isNaN(targetValue) && targetValue !== undefined) {
      const { range, interval } = getAppropriateRange(
        targetValue,
        actualValue,
        14,
      )
      const numberOfIndices = range / interval

      for (let i = 0; i < numberOfIndices; i++) {
        // If on a small screen only show every other axis label
        if (isSmallScreen() && i % 2 == 1) continue

        let indiceValue = i * interval
        axisLabels.push({
          label: addValueIndicator(suffix, formatRangeValue(indiceValue)),
          percent: (indiceValue / range) * 100,
        })
      }

      if (!isSmallScreen()) {
        // last number in progress bar
        axisLabels.push({
          label: addValueIndicator(suffix, formatRangeValue(range)),
          percent: 100,
        })
      }

      // markers in top of progress bar
      markers.push({
        label: addValueIndicator(suffix, formatRangeValue(targetValue)),
        percent: (targetValue / range) * 100,
      })

      // marker in bottom of bar
      var actualValueLabel =
        actualValue && addValueIndicator(suffix, formatRangeValue(actualValue))

      // progress width (not total width)
      var percentFilled = (actualValue / range) * 100
    }

    return {
      markers,
      axisLabels,
      percentFilled,
      actualValueLabel,
    }
  }, [targetValue, actualValue, suffix])

  return (
    <BulletChart
      percentFilled={percentFilled}
      actualValueLabel={actualValueLabel}
      actualValueClassName={actualValueClassName}
      axisLabels={axisLabels}
      className={className}
      markers={markers}
      horizontalBarClassName={props.horizontalBarClassName}
      horizontalBarInnerClassName={props.horizontalBarInnerClassName}
    >
      {children}
    </BulletChart>
  )
}

export default memo(BulletChartWrapper)

/**
 * Formats a number to be displayed on an axis
 * @param {number} val
 * @returns {string}
 */
function formatRangeValue(val) {
  if (Math.round(val) == val) return val + ''
  var result = val.toFixed(2)
  if (result.endsWith('00')) return result.slice(0, -3)
  return result
}

/**
 * Add in indicator for progress bar value
 * @param {string} indicator %, $, £, €...
 * @param {number} value 10 20 33 1.2 99.0055 value of progress bar
 * @returns {string}
 */
const addValueIndicator = (indicator, value) =>
  indicator === '%' ? value + indicator : indicator + value



// WEBPACK FOOTER //
// ./src/Components/Stats/BulletChart/index.js